import React, { useState, useEffect, useCallback } from "react";
import * as XLSX from 'xlsx';
import "./MyView.scss";
import FiltersSidebar from "../../Components/FiltersSidebar/FiltersSidebar";
import TopBar from "../../Components/TopBar/TopBar";
import ExpertCard from "../../Components/ExpertCard/ExpertCard";
import PublishEditModal from "../../Components/PublishEditModal/PublishEditModal";
import { serverAddr } from "../../networkConfig";

const PAGE_SIZE = 4;

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    const formatted = date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    });
    return `${formatted} IST`;
  } catch {
    return timestamp;
  }
};

export default function MyView() {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([
    "Live",
    "Yet To Live",
    "Updated",
  ]);

  const [publishOpen, setPublishOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const activeFiltersCount = selectedFilters.length;

  const [cardsData, setCardsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [exitConfirmId, setExitConfirmId] = useState(null);
  const [exitSuccess, setExitSuccess] = useState(false);
  const [exitError, setExitError] = useState(null);

  useEffect(() => {
    fetchMyViews(1);
  }, []);

  const fetchMyViews = async (page) => {
    try {
      setLoading(true);
      const response = await serverAddr.post("/publisher/my_views/", {
        page_no: page,
        page_per_entries: PAGE_SIZE,
      });

      const rawData = response?.data;
      const data = Array.isArray(rawData) ? rawData : (rawData?.data || []);
      const paginationInfo = response?.pagination || rawData?.pagination || null;

      const formatted = data.map((item) => {
        // scrip_title is e.g. "AERO/USDT" — use part before "/" as display symbol
        const coinName = item.scrip_title || '';
        const symbolLetter = coinName.split('/')[0]?.[0]?.toUpperCase() || '?';

        return {
          id: item.id,
          coin: coinName,
          symbol: symbolLetter,
          price: item.price ?? 0,
          change: item.price_change ?? 0,
          percent: item.perc_diff ?? 0,
          date: formatTimestamp(item.published_on),
          risk: item.stop_loss_1 ?? 0,
          supportLow: item.entry_min ?? 0,
          supportHigh: item.entry_max ?? 0,
          resistanceLow: item.direction === 'LONG' ? (item.target_1 ?? 0) : (item.target_2 ?? 0),
          resistanceHigh: item.direction === 'LONG' ? (item.target_2 ?? 0) : (item.target_1 ?? 0),
          gap: item.remaining_target ?? 0,
          cmp: item.price ?? 0,
          thumbsDown: 0,
          thumbsUp: 0,
          direction: item.direction || 'LONG',
          leverage: item.leverage || null,
          expertName: item.expert_name || null,
          confidenceScore: item.confidence_score || null,
          tags: item.tags || [],
          note: item.note || '',
          chart: item.chart || null,
          targetPeriod: item.target_period || '',
          contractType: item.contract_type || '',
          status: item.status ?? '',
        };
      });

      setCardsData(formatted);
      setPagination(paginationInfo);
    } catch (error) {
      console.error("Error fetching my views:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const card = cardsData.find((item) => item.id === id);
    setSelectedCard(card);
    setPublishOpen(true);
  };

  const handleExit = (id) => {
    setExitConfirmId(id);
  };

  const confirmExit = async () => {
    try {
      await serverAddr.get(`/publisher/exit_reco/${exitConfirmId}`);
      setExitConfirmId(null);
      setExitSuccess(true);
      setTimeout(() => setExitSuccess(false), 3000);
      fetchMyViews(currentPage);
    } catch (error) {
      const message = error?.error || error?.detail || 'Failed to exit recommendation.';
      setExitConfirmId(null);
      setExitError(message);
      setTimeout(() => setExitError(null), 3000);
    }
  };

const handleExport = useCallback(async () => {
    try {
      setExporting(true);

      const response = await serverAddr.post('/publisher/my_views/', {
        page_no: 1,
        page_per_entries: 1000,
      });
      const rawData = response?.data;
      const allData = Array.isArray(rawData) ? rawData : (rawData?.data || []);

      const rows = allData.map((item) => ({
        'Expert Name': item.expert_name ?? '',
        'Coin Name': item.scrip_title ?? '',
        'Direction': item.direction ?? '',
        'Mark Price': item.mark_price ?? '',
        'Lower Entry': item.lower_entry ?? '',
        'Upper Entry': item.upper_entry ?? '',
        'Entry Basis': item.entry_basis ?? '',
        'Target 1': item.target_1 ?? '',
        'Target 2': item.target_2 ?? '',
        'Stoploss': item.stop_loss_1 ?? '',
        'Leverage': item.leverage ?? '',
        'Signal Date': formatTimestamp(item.published_on),
        'Validity': item.target_period ?? '',
        'Logic': item.note ?? '',
        'Tags': Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags ?? ''),
        'Chart URL': item.chart ?? '',
        'Status': item.status ?? '',
        'Status Timestamp': formatTimestamp(item.status_timestamp),
        'Performance': item.performance ?? '',
        'Remaining Target': item.remaining_target ?? '',
        'Risk Reward Ratio 1': item.risk_reward_ratio_1 ?? '',
        'Risk Reward Ratio 2': item.risk_reward_ratio_2 ?? '',
        'Max Profit': item.max_profit ?? '',
        'Max Loss': item.max_loss ?? '',
        'Pushed to which exchanges': item.pushed_exchanges ?? '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'My Views');
      XLSX.writeFile(workbook, 'my_views.xlsx');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExporting(false);
    }
  }, []);

  const handleSearchResult = (coin) => {
    setSearchFilter(coin.name);
  };

  const filteredCards = searchFilter
    ? cardsData.filter((c) => c.coin.toLowerCase().includes(searchFilter.toLowerCase()))
    : cardsData;

  const totalPages = pagination?.total_pages || 1;

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    fetchMyViews(page);
  };


  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="crypto-layout">
      <main className="content">

        <TopBar
          onFilterClick={() => setShowFilters(true)}
          onPublishClick={() => setPublishOpen(true)}
          activeFiltersCount={activeFiltersCount}
          onExportClick={handleExport}
          exporting={exporting}
          onSearchSelect={handleSearchResult}
          onSearchClear={() => setSearchFilter("")}
        />

        {loading ? (
          <div className="myview-loading">
            <div className="myview-spinner" />
          </div>
        ) : filteredCards.length === 0 ? (
          <div className="myview-empty">No views found.</div>
        ) : (
          filteredCards.map((card) => (
            <ExpertCard
              key={card.id}
              data={card}
              onEdit={handleEdit}
              onExit={() => handleExit(card.id)}
            />
          ))
        )}

        {!loading && pagination && (
          <div className="pagination">
            <button
              className="page-btn page-arrow"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.previous_page_number}
            >
              &#8592;
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                className={`page-btn${currentPage === page ? ' active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="page-btn page-arrow"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.next_page_number}
            >
              &#8594;
            </button>

            <span className="page-info">
              {pagination.current_page} / {pagination.total_pages} &nbsp;({pagination.total_entries} total)
            </span>
          </div>
        )}

      </main>

      <PublishEditModal
        open={publishOpen}
        cardData={selectedCard}
        onClose={() => setPublishOpen(false)}
      />

      {showFilters && (
        <div className="filter-popup">
          <div className="filter-popup-body">

            <FiltersSidebar
              isPopup
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              onClose={() => setShowFilters(false)}
            />

          </div>
        </div>
      )}

      {/* EXIT CONFIRM POPUP */}
      {exitConfirmId && (
        <div className="exit-overlay">
          <div className="exit-popup">
            <p>Are you sure you want to exit this recommendation?</p>
            <div className="exit-popup-actions">
              <button className="exit-popup-cancel" onClick={() => setExitConfirmId(null)}>Cancel</button>
              <button className="exit-popup-confirm" onClick={confirmExit}>Yes, Exit</button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS TOAST */}
      {exitSuccess && (
        <div className="exit-toast exit-toast--success">Recommendation exited successfully!</div>
      )}

      {/* ERROR TOAST */}
      {exitError && (
        <div className="exit-toast exit-toast--error">{exitError}</div>
      )}

    </div>
  );
}
